import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { usePutUserLocale } from '@/api/generated/user/user';
import type { LoginResponse } from '@/api/types/auth';
import { reconcileLocaleFromUser } from '@/config/i18n/i18n';
import { LOCALE_STORAGE_KEY, isSupportedTag } from '@/config/i18n/locale';
import { queryKeys } from '@/config/query/queryKeys';

type User = LoginResponse['user'];

function readCachedTag(): string | null {
  return window.localStorage?.getItem(LOCALE_STORAGE_KEY) ?? null;
}

export function useFirstRunProfileGate() {
  const { data: user } = useQuery<User>({
    enabled: false,
    queryKey: queryKeys.auth.user(),
  });

  const queryClient = useQueryClient();
  const updateLocaleMutation = usePutUserLocale();

  useEffect(() => {
    if (user?.locale) {
      reconcileLocaleFromUser(user.locale);
    }
  }, [user?.locale]);

  useEffect(() => {
    if (!user || user.locale) {
      return;
    }
    if (updateLocaleMutation.status !== 'idle') {
      return;
    }
    const cached = readCachedTag();
    if (!isSupportedTag(cached)) {
      return;
    }
    updateLocaleMutation.mutate(
      { data: { locale: cached } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: queryKeys.auth.user() });
        },
      },
    );
  }, [user, queryClient, updateLocaleMutation]);

  const cachedHasValidTag = isSupportedTag(readCachedTag());
  const needsLocale =
    !!user && !user.locale && (!cachedHasValidTag || updateLocaleMutation.isError);
  const needsDisplayName = !!user && (user.displayName?.trim() ?? '').length === 0;
  const isOpen = needsLocale || needsDisplayName;

  return { isOpen, needsDisplayName, needsLocale, user };
}
